import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useNavigate, useParams } from 'react-router';
import {
  EKIPMAN_KONTROL_DURUMU_ETIKETI,
  EKIPMAN_SAHIPLIGI_ETIKETI,
  type EkipmanKontrolDurumu,
} from '@isg/validation';
import {
  useContractors,
  useEkipmanDurumu,
  useEkipmanGuncelle,
  useEkipmanKunyesi,
  useEkipmanRiskMaddeleri,
  useGorevler,
  useEkipmanKapisi,
  useLookup,
} from '../api/queries';
import { tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { RiskEslesmePaneli } from './risk-parts';
import { DurumBolumu, KontrolGecmisiBolumu } from './ekipman-parts';
import { Bolum, KapiSebebi, KayitDetayi, Rozet } from '../sozlesme';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { ANLAM, type RozetAnlami } from '../theme';
import { GorevRozeti } from './gorev-parts';

/**
 * **EKİPMAN DETAY SAYFASI (Y1 — tur 4 artçısı; madde 145 · keşif #8).**
 *
 * ✏️ **KÜNYE MODALDAN ÇIKTI.** Ekipman **bağımsız bir kayıttır** (P1'in kendi
 * listesinde: olay · iş izni · eğitim · **ekipman**) ve bağımsız kaydın
 * künyesi bir listenin üstündeki pencerede yaşamaz. Üç dağınık yüzey —
 * ekleme modali · `/ekipman/:id/durum` · `/ekipman/:id/kontroller` — tek
 * sayfada birleşti (**P5: kayıt detayında sekme yok, bölümler alt alta**;
 * *"çıktıya giden şey sayfanın kendisidir"*).
 *
 * ⚠️ **KÜNYE IZGARADA (P9):** kısa alanlar yan yana; her ekranda yeniden
 * karar verilmesin diye.
 *
 * ⚠️ **"SON KONTROL" BU SAYFADA DÜZENLENEMEZ ve SEBEBİ YANINDA DURUR**
 * (§2.40'ın sunum kolu — tur 4 / K1): tarih kontrol geçmişinden TÜRETİLİR.
 * Alanı düzenlenebilir gösterip yazmayı reddetmek, "yalan söyleyen kontrol"
 * sınıfının ta kendisi olurdu.
 */
export function EkipmanDetayScreen(): React.JSX.Element {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const id = equipmentId ?? '';
  const kunye = useEkipmanKunyesi(id);
  const durum = useEkipmanDurumu(id);
  // ✏️ P13 · B3 — künye + kontrol geçmişi bayrağı `PATCH equipment/:id` ve
  // kontrol uçlarının adım listesinden (şapka → bahçe → arşiv). Eskiden yalnız
  // GÖREV şapkası (`sapka`) okunuyordu: birleşimi görmüyor, bahçeyi ve arşivi
  // hiç sormuyordu.
  const ekipmanKapisi = useEkipmanKapisi(id || undefined);
  const yazabilir = ekipmanKapisi.data?.yazabilir ?? false;

  return (
    <Stack>
      <QueryStatus queries={[kunye, durum]} kayit />
      {kunye.data && durum.data && (
        <KayitDetayi
          testId="ekipman-detay"
          geriYol="/ekipman"
          geriEtiket="İş ekipmanlarına dön"
          baslik={kunye.data.ad}
          ustBilgi={
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {durum.data.workplaceAd} · {kunye.data.tipAd || '—'}
              </Text>
              <Rozet
                anlam={kunye.data.durum === 'aktif' ? 'olumlu' : 'bilgi'}
                testId="ekipman-durum-rozeti"
              >
                {kunye.data.durum === 'aktif' ? 'Aktif' : 'Pasif'}
              </Rozet>
              <KontrolRozeti durum={kunye.data.kontrolDurumu} />
            </Group>
          }
        >
          <KunyeBolumu ekipman={kunye.data} yazabilir={yazabilir} />
          {ekipmanKapisi.data && (
            <KapiSebebi
              durum={ekipmanKapisi.data}
              nesne="Ekipman künyesini"
              fiil="düzenler"
              testId="ekipman-kunye-yazamama-sebebi"
            />
          )}
          <DurumBolumu equipmentId={id} veri={durum.data} />
          <KontrolGecmisiBolumu equipmentId={id} ekipmanKapisi={ekipmanKapisi.data} />
          <GorevlerBolumu
            equipmentId={id}
            onAc={(occurrenceId) => navigate(`/ekipman/gorevler/${occurrenceId}`)}
          />
          <RiskBolumu equipmentId={id} />
        </KayitDetayi>
      )}
    </Stack>
  );
}

/**
 * Kontrol durumu rozeti — ⚠️ **ANLAM taşır, RENK taşımaz** (P3-b): eşleme
 * `theme.ts`tedir. Eşiğin kendisi `@isg/validation`ın TEK okuyucusundan
 * gelir; ekran onu yeniden hesaplamaz.
 */
function KontrolRozeti({ durum }: { durum: EkipmanKontrolDurumu }): React.JSX.Element {
  // ✏️ MADDE 302: durum SUNUCUDAN (saha günü) — tarayıcı saati gün kararı vermez.
  const anlam: RozetAnlami =
    durum === 'marj'
      ? 'engel'
      : durum === 'yaklasiyor'
        ? 'dikkat'
        : durum === 'bilinmiyor'
          ? 'bilgi'
          : 'olumlu';
  return (
    <Rozet anlam={anlam} testId="ekipman-kontrol-rozeti">
      {EKIPMAN_KONTROL_DURUMU_ETIKETI[durum]}
    </Rozet>
  );
}

/**
 * KÜNYE — P9 ızgarası + tek "Kaydet". ⚠️ **DÜĞME KAPSAMINI SÖYLER** (madde
 * 140 dersi) ve kaydedilmemiş değişiklik ROZETLE işaretlenir.
 */
function KunyeBolumu({
  ekipman,
  yazabilir,
}: {
  ekipman: NonNullable<ReturnType<typeof useEkipmanKunyesi>['data']>;
  yazabilir: boolean;
}): React.JSX.Element {
  const guncelle = useEkipmanGuncelle();
  const tipler = useLookup('equipment_tip');
  // K2 — kapasite birimi kendi sözlük listesinden (yeni liste kodu).
  const birimler = useLookup('kapasite_birimi');
  const yukleniciler = useContractors();

  const [tipId, setTipId] = useState<string | null>(ekipman.tipId);
  const [ad, setAd] = useState(ekipman.ad);
  const [seriNo, setSeriNo] = useState(ekipman.seriNo ?? '');
  const [demirbasNo, setDemirbasNo] = useState(ekipman.demirbasNo ?? '');
  const [imalatci, setImalatci] = useState(ekipman.imalatci ?? '');
  const [imalatYili, setImalatYili] = useState<number | string>(ekipman.imalatYili ?? '');
  const [kapasiteDeger, setKapasiteDeger] = useState<number | string>(ekipman.kapasiteDeger ?? '');
  const [kapasiteBirimId, setKapasiteBirimId] = useState<string | null>(ekipman.kapasiteBirimId);
  const [contractorId, setContractorId] = useState<string | null>(ekipman.contractorId);

  const kirli =
    ad !== ekipman.ad ||
    tipId !== ekipman.tipId ||
    seriNo !== (ekipman.seriNo ?? '') ||
    demirbasNo !== (ekipman.demirbasNo ?? '') ||
    imalatci !== (ekipman.imalatci ?? '') ||
    String(imalatYili) !== String(ekipman.imalatYili ?? '') ||
    String(kapasiteDeger) !== String(ekipman.kapasiteDeger ?? '') ||
    kapasiteBirimId !== ekipman.kapasiteBirimId ||
    contractorId !== ekipman.contractorId;
  // Madde 221 — kaydedilmemiş künyeyle sayfadan çıkış YÖNLENDİRİCİDE uyarılır.
  useKaydedilmemisIcerik(kirli ? 1 : 0, 'künye değişikliği');

  return (
    <Bolum baslik="Künye" testId="ekipman-kunye">
      {guncelle.isError && (
        <Alert color={ANLAM.engel} role="alert" mb="xs" data-testid="ekipman-kunye-hata">
          {guncelle.error.message}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
        <TextInput
          label="Ad"
          value={ad}
          disabled={!yazabilir}
          onChange={(olay) => setAd(olay.currentTarget.value)}
          data-testid="ekipman-kunye-ad"
        />
        <Select
          label="Tip"
          searchable
          disabled={!yazabilir}
          data={(tipler.data ?? []).map((t) => ({ value: t.id, label: t.ad }))}
          value={tipId}
          onChange={setTipId}
          data-testid="ekipman-kunye-tip"
        />
        <TextInput
          label="Seri no"
          value={seriNo}
          disabled={!yazabilir}
          onChange={(olay) => setSeriNo(olay.currentTarget.value)}
          data-testid="ekipman-kunye-seri"
        />
        <TextInput
          label="Demirbaş no"
          value={demirbasNo}
          disabled={!yazabilir}
          onChange={(olay) => setDemirbasNo(olay.currentTarget.value)}
          data-testid="ekipman-kunye-demirbas"
        />
        <TextInput
          label="İmalatçı"
          value={imalatci}
          disabled={!yazabilir}
          onChange={(olay) => setImalatci(olay.currentTarget.value)}
          data-testid="ekipman-kunye-imalatci"
        />
        <NumberInput
          label="İmalat yılı"
          min={1900}
          max={2100}
          allowDecimal={false}
          disabled={!yazabilir}
          value={imalatYili}
          onChange={setImalatYili}
          data-testid="ekipman-kunye-imalat-yili"
        />
        {/* K2 — DEĞER ve BİRİM BİRLİKTE YAŞAR; kural sunucuda (DB CHECK +
            uç), ekran yalnız ikisini yan yana koyar. */}
        <NumberInput
          label="Kapasite"
          min={0}
          disabled={!yazabilir}
          value={kapasiteDeger}
          onChange={setKapasiteDeger}
          data-testid="ekipman-kunye-kapasite"
        />
        <Select
          label="Kapasite birimi"
          clearable
          disabled={!yazabilir}
          data={(birimler.data ?? []).map((b) => ({ value: b.id, label: b.ad }))}
          value={kapasiteBirimId}
          onChange={setKapasiteBirimId}
          data-testid="ekipman-kunye-kapasite-birimi"
        />
        {/* K2 — SAHİPLİK TEK KOLON: boş = işyerinin şirketi (madde 80 deseni). */}
        <Select
          label="Sahiplik"
          description={`Boş bırakılırsa: ${EKIPMAN_SAHIPLIGI_ETIKETI.kendi}`}
          clearable
          searchable
          disabled={!yazabilir}
          data={(yukleniciler.data ?? []).map((c) => ({ value: c.id, label: c.ad }))}
          value={contractorId}
          onChange={setContractorId}
          data-testid="ekipman-kunye-sahiplik"
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm" mt="md">
        <SaltOkunur
          etiket="Kontrol periyodu (ay)"
          deger={ekipman.kontrolPeriyoduAy?.toString() ?? '—'}
        />
        <SaltOkunur
          etiket="Son kontrol"
          deger={tarihGoster(ekipman.sonKontrol)}
          testId="ekipman-kunye-son-kontrol"
        />
        <SaltOkunur etiket="Sonraki kontrol" deger={tarihGoster(ekipman.sonrakiKontrol)} />
      </SimpleGrid>
      {/* ⚠️ §2.40'ın SUNUM KOLU: olmayan bir alanın SEBEBİ yanında durur. */}
      <Text size="xs" c="dimmed" mt={4} data-testid="ekipman-kunye-turetme-notu">
        Son kontrol tarihi aşağıdaki kontrol geçmişinden türetilir (en son "uygun" sonuçlanan
        kontrol) ve künyeden yazılamaz. Periyot ekipman tipinden çözülür; "—" ise bu işyerinde bu
        tipin periyodu tanımlı değildir ve sonraki kontrol tarihi hesaplanmaz.
      </Text>

      {yazabilir && (
        <Group mt="md" gap="xs">
          <Button
            size="xs"
            disabled={!kirli}
            loading={guncelle.isPending}
            data-testid="ekipman-kunye-kaydet"
            onClick={() =>
              guncelle.mutate({
                id: ekipman.id,
                input: {
                  ad: ad.trim(),
                  ...(tipId ? { tipId } : {}),
                  seriNo: seriNo.trim() === '' ? null : seriNo.trim(),
                  demirbasNo: demirbasNo.trim() === '' ? null : demirbasNo.trim(),
                  imalatci: imalatci.trim() === '' ? null : imalatci.trim(),
                  imalatYili: imalatYili === '' ? null : Number(imalatYili),
                  kapasiteDeger: kapasiteDeger === '' ? null : Number(kapasiteDeger),
                  kapasiteBirimId,
                  contractorId,
                },
              })
            }
          >
            Künyeyi kaydet
          </Button>
          {kirli && (
            <Rozet anlam="dikkat" testId="ekipman-kunye-kirli">
              Kaydedilmemiş değişiklik var
            </Rozet>
          )}
        </Group>
      )}
    </Bolum>
  );
}

function SaltOkunur({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={0} data-testid={testId}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm">{deger}</Text>
    </Stack>
  );
}

/**
 * BAĞLI GÖREVLER — okuma yüzeyi. ⚠️ **"Görevi kapat" DÜĞMESİ YOKTUR ve
 * eklenmez** (§22.6a kilidi): görev form doldurulunca kapanır, `yapildi`
 * damgasının tek anlamı *"bu dönemde bir kontrol kaydı doğdu"*dur.
 */
function GorevlerBolumu({
  equipmentId,
  onAc,
}: {
  equipmentId: string;
  onAc: (occurrenceId: string) => void;
}): React.JSX.Element {
  // ⚠️ Bu bölüm HER DURUMU gösterir (`hepsi`): kaçırılmış dönemler bir
  // GEÇMİŞ PERFORMANS KAYDIDIR (G2-a) ve kaydın kendi sayfasından silinmez.
  const gorevler = useGorevler({ durum: 'hepsi', ekipmanId: equipmentId, sayfa: 1 });
  const satirlar = gorevler.data?.satirlar ?? [];
  return (
    <Bolum
      baslik="Periyodik görevler"
      aciklama="Bu ekipmanın açık ve kapanmış görev dönemleri. Görev, kontrol formu doldurulunca kapanır."
      testId="ekipman-gorev-listesi"
    >
      {satirlar.length === 0 ? (
        <Text size="sm" c="dimmed">
          Bu ekipman için henüz görev üretilmedi.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Dönem</Table.Th>
              <Table.Th>Vade</Table.Th>
              <Table.Th>Kalem</Table.Th>
              <Table.Th>Durum</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {satirlar.map((g) => (
              <Table.Tr
                key={g.id}
                data-testid="ekipman-gorev-satiri"
                style={{ cursor: 'pointer' }}
                onClick={() => onAc(g.id)}
              >
                <Table.Td>{g.donem}</Table.Td>
                <Table.Td>{tarihGoster(g.vadeTarihi)}</Table.Td>
                <Table.Td>{g.kalemAd}</Table.Td>
                <Table.Td>
                  {/* Durum → rozet anlamı eşlemesi TEK YERDE (gorev-parts). */}
                  <GorevRozeti satir={g} testId="ekipman-gorev-rozeti" />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Bolum>
  );
}

/**
 * HEDEF SORGU 2 (F3B·S9 — Kapsam §16.12/12): *"yeni ekipman geldi, hemen
 * madde ekleriz"*.
 *
 * ✏️ **LİSTEDEN BURAYA TAŞINDI (Y1):** listede bir seçici olarak duruyordu ve
 * sunucu sayfalaması gelince o seçici yalnız **görünen sayfayı** tanıyacaktı
 * (*"ekipmanım listede yok"* — oysa vardı, sonraki sayfadaydı). Ekipmanın risk
 * maddeleri ekipmanın **kendi sayfasına** aittir; seçim adımı da böylece
 * ortadan kalktı. OKUMA YÜZEYİ, kapı DEĞİL.
 */
function RiskBolumu({ equipmentId }: { equipmentId: string }): React.JSX.Element {
  const maddeler = useEkipmanRiskMaddeleri(equipmentId);
  return (
    <Card withBorder padding="md" data-testid="ekipman-risk-karti">
      <RiskEslesmePaneli
        baslik="İlgili risk maddeleri"
        veri={maddeler.data}
        testId="ekipman-risk-paneli"
      />
    </Card>
  );
}
